import { createReducer, Action, on } from '@ngrx/store';
import * as Adapters from './announcement.adapters';
import * as Actions from './announcement.actions';
import { initialState, State } from './announcement.state';
import { isManager, ALL_EMPLOYEES, Recipient } from '../../../interfaces';
import { Announcements } from '@app/core/async-services/http/versioned/interfaces';
import moment from 'moment';
import { API } from '@app/core/models';

const communicationReducer = createReducer<State>(
  initialState,
  on(Actions.loadHotels, (state) => {
    return {
      ...state,
      api: {
        ...state.api,
        getHotels: API.LoadingState.LOADING,
      },
    };
  }),
  on(Actions.loadManagers, (state) => {
    return {
      ...state,
      api: {
        ...state.api,
        getManagers: API.LoadingState.LOADING,
      },
    };
  }),
  on(Actions.loadHotelsSuccess, (state, { response }) => {
    const hotels = response.payload.hotels;
    const departments = hotels.flatMap((hotel) => hotel.departments);
    return {
      ...state,
      hotels: Adapters.hotels.setAll(hotels, state.hotels),
      departments: Adapters.departments.setAll(departments, state.departments),
      api: {
        ...state.api,
        getHotels: API.LoadingState.LOADED,
      },
    };
  }),
  on(Actions.loadManagersSuccess, (state, { response }) => {
    const { hotelManagers, corporateManagers } = response.payload;
    return {
      ...state,
      hotelManagers: Adapters.managers.setAll(
        hotelManagers,
        state.hotelManagers,
      ),
      corporateManagers: Adapters.managers.setAll(
        corporateManagers,
        state.corporateManagers,
      ),
      api: {
        ...state.api,
        getManagers: API.LoadingState.LOADED,
      },
    };
  }),
  on(Actions.addHotelRecipient, (state, { id }) => ({
    ...state,
    recipients: [
      ...(state.recipients as Recipient[]),
      state.hotels.entities[id] as Recipient,
    ],
  })),
  on(Actions.addDepartmentRecipient, (state, { id }) => ({
    ...state,
    recipients: [
      ...(state.recipients as Recipient[]),
      state.departments.entities[id] as Recipient,
    ],
  })),
  on(Actions.addHotelManagerRecipient, (state, { id }) => ({
    ...state,
    recipients: [
      ...(state.recipients as Recipient[]),
      state.hotelManagers.entities[id] as Recipient,
    ],
  })),
  on(Actions.addCorporateManagerRecipient, (state, { id }) => ({
    ...state,
    recipients: [
      ...(state.recipients as Recipient[]),
      state.corporateManagers.entities[id] as Recipient,
    ],
  })),
  on(Actions.removeRecipient, (state, { index }) => ({
    ...state,
    recipients: [
      ...state.recipients.slice(0, index),
      ...state.recipients.slice(index + 1),
    ],
  })),
  on(Actions.clearRecipients, (state) => ({
    ...state,
    recipients: [],
  })),
  on(Actions.clearRecipientsAndDraft, (state) => ({
    ...state,
    announcementDraft: '',
    recipients: [],
  })),
  on(Actions.addAllEmployees, (state) => {
    if (!state.recipients.includes(ALL_EMPLOYEES)) {
      return {
        ...state,
        recipients: [...state.recipients, ALL_EMPLOYEES],
      };
    }
    return { ...state };
  }),
  on(Actions.addAllHotelManagers, (state) => {
    const managerRecipientIds = state.recipients
      .filter(isManager)
      .map(({ webUserId }) => webUserId);
    const managers: Announcements.Manager[] = [];
    Object.values(state.hotelManagers.entities).forEach((manager) => {
      if (
        manager !== undefined &&
        !managerRecipientIds.includes(manager.webUserId) &&
        manager.userStatus === 'activated'
      ) {
        managers.push(manager);
      }
    });
    return {
      ...state,
      recipients: [...state.recipients, ...managers],
    };
  }),
  on(Actions.addAllCorporateManagers, (state) => {
    const managerRecipientIds = state.recipients
      .filter(isManager)
      .map(({ webUserId }) => webUserId);
    const managers: Announcements.Manager[] = [];
    Object.values(state.corporateManagers.entities).forEach((manager) => {
      if (
        manager !== undefined &&
        !managerRecipientIds.includes(manager.webUserId) &&
        manager.userStatus === 'activated'
      ) {
        managers.push(manager);
      }
    });
    return {
      ...state,
      recipients: [...state.recipients, ...managers],
    };
  }),
  on(Actions.loadExistingAnnouncement, (state) => ({
    ...state,
    api: {
      ...state.api,
      getAnnouncement: API.LoadingState.LOADING,
    },
  })),
  on(Actions.loadExistingAnnouncementSuccess, (state, { payload }) => ({
    ...state,
    api: {
      ...state.api,
      getAnnouncement: API.LoadingState.LOADED,
    },
    channel: payload.channel,
    message: {
      ...payload.messages[0],
      createdOn: moment
        .utc(payload.messages[0].createdOn)
        .local()
        .format('ddd MMM DD h:mm a')
        .toString(),
    },
  })),
  on(Actions.resetViewAnnouncementState, (state) => ({
    ...state,
    api: {
      ...state.api,
      getAnnouncement: API.LoadingState.INIT,
    },
    message: null,
    channel: '',
  })),
  on(Actions.saveAnnouncementDraft, (state, { announcementDraft }) => ({
    ...state,
    announcementDraft,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return communicationReducer(state, action);
}
